<script setup lang="ts">
import VueMarkdown from 'vue-markdown-render'
import { toast, type ToastOptions } from 'vue3-toastify'
import { useI18n } from 'vue-i18n'
import { useStrapiKitsListPageWithNoLocaleFilter } from '~/composables/useStrapiKits'
import { useTransactionalPageResource } from '~/composables/transactionalPage/useTransactionalPageResource'
import useAlgoliaEventCarouselSchema from '~/composables/schemaOrg/useAlgoliaEventCarouselSchema'
import { useTransactionalProductSchema } from '~/composables/schemaOrg/useTransactionalProductSchema'
import useTransactionalPageHead from '~/composables/transactionalPage/useTransactionalPageHead'
import { EventListPageType } from '~/composables/types/strapiEventListPage'
import useKitProductSchema from '~/composables/schemaOrg/useKitProductSchema'

const route = useRoute()
const { t, locale } = useI18n()
const boxSlug = route.params.category?.toString() || ''

// Get data without locale filter
const strapiKitsListPageWithNoLocaleFilter =
  await useStrapiKitsListPageWithNoLocaleFilter({
    slug: boxSlug,
  })

// Apply redirect logic if needed
const transactionalPageResource = await useTransactionalPageResource({
  strapiEventListPageWithNoLocaleFilter: strapiKitsListPageWithNoLocaleFilter,
})

// Get the correct data for current locale
const kitsObject = computed(() => {
  const entry =
    transactionalPageResource?.data?.value?.find(
      (entry) => entry?.locale.toLowerCase() === locale.value
    ) || transactionalPageResource?.data?.value?.[0]

  if (!entry) return null

  const result =
    strapiEventListPageGetters.getLocalizationByLocale(entry, locale.value) ||
    entry

  return result
})

// Handle errors
if (strapiKitsListPageWithNoLocaleFilter.error.value) {
  toast(t('general.error'), {
    autoClose: 2000,
    type: 'error',
    position: toast.POSITION.TOP_CENTER,
    multiple: false,
  } as ToastOptions)
}

if (!kitsObject.value) {
  throw showError({ statusCode: 404, statusMessage: 'Page Not Found' })
}

const { algoliaResults, status: algoliaStatus } = await useBoxEvents({
  category: kitsObject.value?.categorySlug || '',
})

if (algoliaStatus.value === 'error') {
  toast(t('general.error'), {
    autoClose: 2000,
    type: 'error',
    position: toast.POSITION.TOP_CENTER,
    multiple: false,
  } as ToastOptions)

  throw showError({ statusCode: 404, statusMessage: 'Page Not Found' })
}

const products = computed(() => {
  return algoliaResults.value?.results?.[0]?.hits || []
})

const cardShowcase = computed(() => {
  return Object.keys(kitsObject.value?.showcase || {})
    ?.map((key) => kitsObject.value?.showcase[key])
    .filter((cardDetails) => cardDetails?.id)
    .map((cd) => ({
      ...cd,
      imageUrl: strapiImageGetters.getImageUrl(cd.image),
    }))
})

// Schema markup setup
const { script: productSchemas } = useKitProductSchema(products.value)

const searchHeading = computed(
  () => kitsObject.value?.searchHeading || kitsObject.value?.title || ''
)

const eventCount = computed(() => products.value?.length || 0)

// Create schema markup for kits - use productSchemas inside eventCarouselSchemas
const { script: eventCarouselSchemas } = useAlgoliaEventCarouselSchema(
  productSchemas.value.innerHTML['@graph']
)

const { shouldAppendTransactionalProductSchema, schemaObj } =
  await useTransactionalProductSchema({
    locationSlug: computed(() => undefined), // kits don't have a location segment
    tagSlug: computed(() => boxSlug),
    categorySlug: computed(() => kitsObject.value?.categorySlug),
    eventListPageData: kitsObject,
    pageType: EventListPageType.KITS,
    searchHeading,
    eventCount,
    hits: products,
  })

useTransactionalPageHead(
  EventListPageType.KITS,
  kitsObject.value,
  {
    script: [
      ...(products.value?.length > 0 ? [eventCarouselSchemas] : []),
      ...(shouldAppendTransactionalProductSchema.value && eventCount.value
        ? [schemaObj]
        : []),
    ],
  },
  {
    hits: products,
  }
)
</script>

<template>
  <div>
    <TransactionalPageBreadcrumbs
      class="my-4"
      :type="EventListPageType.KITS"
      :event-list-page-obj="kitsObject"
    />

    <div class="event-list-page__section mb-6">
      <div
        class="relative h-[80px] md:h-auto rounded-xl overflow-hidden md:overflow-visible flex items-center"
      >
        <NuxtImg
          preload
          class="card-image object-cover w-full md:hidden"
          src="/_nuxt3-static/general/transactional-pages-banner.webp"
          alt="Ein Barkeeper gießt Cocktails in zwei Gläser."
          loading="eager"
          fetchpriority="high"
        />

        <div
          class="w-full h-full bg-blend-hard-light bg-gradient-to-r from-white to-transparent absolute"
        />

        <Heading
          v-if="kitsObject.searchHeading || kitsObject.title"
          class="absolute md:relative"
          h-class="text-h5 !text-teal-900"
          :title="kitsObject.searchHeading || kitsObject.title"
          :level="1"
        />
      </div>
    </div>

    <ProductShowcase :products="products" />

    <NuxtLazyHydrate when-visible>
      <TransactionalPageCardListFromStrapi
        v-if="
          strapiEventListPageGetters.getHorizontalCardList(kitsObject).length
        "
        :event-list-page="kitsObject"
        class="pt-10"
        data-test-id="horizontal-card-list"
      />
    </NuxtLazyHydrate>

    <NuxtLazyHydrate v-if="kitsObject?.description" when-visible>
      <vue-markdown
        class="prose my-5 w-full max-w-none"
        :source="kitsObject?.description || ''"
      />
    </NuxtLazyHydrate>

    <div class="mt-10">
      <Heading
        :level="2"
        :title="strapiEventListPageGetters.getShowcaseTitle(kitsObject)"
        class="kft-heading--left mb-10"
      />
      <NuxtLazyHydrate when-visible>
        <SimpleCardShowcase
          :showcase-items="cardShowcase"
          data-test-id="simple-showcase"
        />
      </NuxtLazyHydrate>
    </div>
  </div>
</template>
